import { ReactComponent as LockSVG } from "@/assets/icons/ic_lock.svg";
import BaseCollapse from "@/components/reusables/BaseCollapse";
import BaseDropdown from "@/components/reusables/dropdowns/BaseDropdown";
import {
    useGetShopifyLocationMappingsQuery,
    useGetShopifyLocationsQuery,
} from "@/redux/features/integrations/shopifyApi";
import { useGetModuleLocationsQuery } from "@/redux/features/locations/locationsApi";
import { ArrowRight, Check } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { generateLocationLabel } from "../LocationsMapper/utils";

type DisplayValue = {
    id: string;
    label: string;
};

export default function LocationsDisplayer() {
    const { t } = useTranslation("", { keyPrefix: "imports.modules.shopify.importModal.locationsDisplayer" });
    const { data: existingMappings, isFetching: isExistingMappingsFetching } =
        useGetShopifyLocationMappingsQuery();

    const { data: suppliLocations, isFetching: isSuppliLocationsFetching } =
        useGetModuleLocationsQuery("integrations");
    const { data: shopifyLocations, isFetching: isShopifyLocationsFetching } =
        useGetShopifyLocationsQuery();

    // Rebuild displayable rows by joining saved mappings with freshly fetched Shopify/Suppli locations.
    const reconstructedMappings = useMemo(() => {
        if (!existingMappings?.length) return [];

        // Precompute lookups to avoid repeated array traversals (O(1) lookups).
        const shopifyMap = new Map((shopifyLocations ?? []).map((loc) => [loc.shopify_id, loc]));

        const suppliMap = new Map(
            (suppliLocations ?? []).map((loc) => [
                `${loc.store.id}_${loc.section?.id ?? "null"}`,
                loc,
            ])
        );

        return existingMappings.map((mapping) => {
            // Match mapping to Shopify location by id; fall back to "Unknown" if stale.
            const shopifyLocation = shopifyMap.get(mapping.shopify_location_id);

            // Match mapping to Suppli location by store/section; tolerate missing references.
            const suppliLocation = suppliMap.get(
                `${mapping.location.store_id ?? "null"}_${mapping.location.section_id ?? "null"}`
            );

            const shopifyDisplay: DisplayValue = {
                id: mapping.shopify_location_id,
                label: shopifyLocation
                    ? generateLocationLabel(
                          shopifyLocation.location.store_name,
                          shopifyLocation.location.section_name
                      )
                    : t("fields.shopify.unknownValue"),
            };

            const suppliDisplay: DisplayValue = {
                id: `${mapping.location.store_id ?? "null"}_${mapping.location.section_id ?? "null"}`,
                label: suppliLocation
                    ? generateLocationLabel(suppliLocation.store.name, suppliLocation.section?.name)
                    : t("fields.suppli.unknownValue"),
            };

            return {
                id: `${mapping.shopify_location_id}-${mapping.location.store_id ?? "store"}-${
                    mapping.location.section_id ?? "section"
                }`,
                shopify: shopifyDisplay,
                suppli: suppliDisplay,
            };
        });
    }, [
        existingMappings,
        shopifyLocations,
        suppliLocations,
        isExistingMappingsFetching,
        isSuppliLocationsFetching,
        isShopifyLocationsFetching,
    ]);

    const isBaseCollapseLoading =
        isExistingMappingsFetching || isSuppliLocationsFetching || isShopifyLocationsFetching;

    // Track if mappings were ever rendered; hide initial loading when empty, but show loaders on updates.
    const hasRenderedMappingsRef = useRef(false);
    useEffect(() => {
        if (existingMappings?.length) {
            hasRenderedMappingsRef.current = true;
        }
    }, [existingMappings?.length]);

    // On first mount while fetching, hide block if nothing is available yet.
    if (
        isBaseCollapseLoading &&
        !hasRenderedMappingsRef.current &&
        !(existingMappings?.length ?? 0)
    ) {
        return null;
    }

    // Hide the block entirely when there are no saved mappings.
    if (!isExistingMappingsFetching && !(existingMappings?.length ?? 0)) {
        return null;
    }

    return (
        <BaseCollapse
            loading={isBaseCollapseLoading}
            className="bg-[#F8F8F8] border-transparent hover:border-[#EDEDED]"
        >
            <BaseCollapse.Header className="flex items-center justify-between bg-transparent">
                <div>
                    <div className="text-[#5A5587] font-semibold">{t("title")}</div>
                    <div className="text-[#686868] text-sm font-thin">
                        {t("description")}
                    </div>
                </div>
                <BaseCollapse.Chevron />
            </BaseCollapse.Header>
            <BaseCollapse.Body className="!border-none">
                {/* Read-only mapping rows mirror mapper visuals: disabled dropdowns + arrow */}
                <div className="flex flex-col">
                    <div className="max-h-[450px] overflow-y-auto space-y-4">
                        {reconstructedMappings.map((mapping) => (
                            <div key={mapping.id} className="flex items-center gap-[16px]">
                                <LockSVG className="w-6 h-6 text-[#D9D6DA]" />
                                <div className="flex-1">
                                    <BaseDropdown
                                        options={[]}
                                        value={mapping.shopify}
                                        getter={{
                                            key: (opt) => opt.id,
                                            label: (opt) => opt.label,
                                        }}
                                        placeholder={t("fields.shopify.placeholder")}
                                        disabled
                                        disableClearable
                                        sx={{
                                            "&, & input": {
                                                backgroundColor: "#EDEDED !important",
                                                color: "#686868",
                                            },
                                            "& svg": {
                                                color: "#D9D6DA !important",
                                            },
                                        }}
                                    />
                                </div>
                                <div className="flex items-center">
                                    <ArrowRight className="w-5 h-5 text-[#B1B2E1]" />
                                </div>
                                <div className="flex-1">
                                    <BaseDropdown
                                        options={[]}
                                        value={mapping.suppli}
                                        getter={{
                                            key: (opt) => opt.id,
                                            label: (opt) => opt.label,
                                        }}
                                        placeholder={t("fields.suppli.placeholder")}
                                        disabled
                                        disableClearable
                                        sx={{
                                            "&, & input": {
                                                backgroundColor: "#EDEDED !important",
                                                color: "#686868",
                                            },
                                            "& svg": {
                                                color: "#D9D6DA !important",
                                            },
                                        }}
                                    />
                                </div>
                                <Check size={18} className="text-[#A6DD4C]" />
                            </div>
                        ))}
                    </div>
                </div>
            </BaseCollapse.Body>
        </BaseCollapse>
    );
}
